import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useBin, useSales } from "../../query/hooks";
import {
  CubeIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  ClockIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import LogoLoader from "../Common/LogoLoader";

const SellerDashboard = () => {
  const { user } = useAuth();
  const binQuery = useBin(!!user);
  const salesQuery = useSales(!!user);
  const binItems = binQuery.data?.materials || [];
  const sales = Array.isArray(salesQuery.data) ? salesQuery.data : [];
  const recentSales = sales.slice(0, 5);
  const loading = binQuery.isLoading || salesQuery.isLoading;

  const totalEarnings = sales
    .filter(sale => sale.status === 'paid')
    .reduce((sum, sale) => sum + (sale.totalPrice || 0), 0);
  
  const pendingEarnings = sales
    .filter(sale => sale.status === 'processing')
    .reduce((sum, sale) => sum + (sale.totalPrice || 0), 0);

  const stats = [
    {
      name: "Items in Bin",
      value: binItems.length,
      icon: ShoppingCartIcon,
      color: "bg-blue-500",
      href: "/bin",
    },
    {
      name: "Total Sales",
      value: sales.length,
      icon: CurrencyDollarIcon,
      color: "bg-green-500",
      href: "/sales",
    },
    {
      name: "Total Earnings",
      value: `₦${totalEarnings.toFixed(2)}`,
      icon: CurrencyDollarIcon,
      color: "bg-green-600",
      href: "/sales",
    },
    {
      name: "Pending Earnings",
      value: `₦${pendingEarnings.toFixed(2)}`,
      icon: ClockIcon,
      color: "bg-yellow-500",
      href: "/sales",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.first_name || user?.username}!
        </h1>
        <p className="text-gray-600 mt-2">
          Ready to sell your waste materials? Browse our materials, add them to
          your bin, and connect with agents.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stat.value}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/materials"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CubeIcon className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">Browse Materials</h3>
              <p className="text-sm text-gray-600">
                View available materials to sell
              </p>
            </div>
          </Link>

          <Link
            to="/bin"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ShoppingCartIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">My Bin</h3>
              <p className="text-sm text-gray-600">
                Manage your selected materials
              </p>
            </div>
          </Link>

          <Link
            to="/agents"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <UserGroupIcon className="h-8 w-8 text-orange-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">Find Agents</h3>
              <p className="text-sm text-gray-600">
                Connect with collection agents
              </p>
            </div>
          </Link>

          <Link
            to="/banks"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CreditCardIcon className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">Bank Details</h3>
              <p className="text-sm text-gray-600">Manage payment accounts</p>
            </div>
          </Link>

          <Link
            to="/sell"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CurrencyDollarIcon className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">Sell Materials</h3>
              <p className="text-sm text-gray-600">Complete your sale</p>
            </div>
          </Link>

          <Link
            to="/sales"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ClockIcon className="h-8 w-8 text-gray-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">Sale History</h3>
              <p className="text-sm text-gray-600">View your past sales</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Recent Sales
        </h2>
        {recentSales.length > 0 ? (
          <div className="space-y-4">
            {recentSales.map((sale) => (
              <div
                key={sale._id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    Sale #{sale._id.slice(-6)}
                  </p>
                  <p className="text-sm text-gray-600">
                    {new Date(sale.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">
                    ₦{sale.totalPrice?.toFixed(2) || "0.00"}
                  </p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      sale.status === "paid"
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {sale.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 text-center py-8">
            No sales yet. Start by browsing materials and adding them to your
            bin!
          </p>
        )}
      </div>
    </div>
  );
};

export default SellerDashboard;
